import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "bread-crumbs"
}
const _hoisted_2 = ["onClick"]
const _hoisted_3 = {
  key: 0,
  class: "entry-icon"
}
const _hoisted_4 = { class: "entry-text" }
const _hoisted_5 = ["onDblclick", "onClick"]
const _hoisted_6 = {
  key: 0,
  class: "entry-icon"
}
const _hoisted_7 = { class: "entry-text" }
const _hoisted_8 = {
  key: 1,
  class: "entry-action"
}
const _hoisted_9 = {
  key: 2,
  class: "divider"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = {
  key: 0,
  class: "entry-icon"
}
const _hoisted_12 = { class: "entry-text" }

import { AttributeDataType } from "@medapro-types/common/common-enum";
import { Branch, Leaf, MultiLevelNavigationConfirmModel, MultiLevelNavigationSelectionModel } from "@medapro-types/common/components/MultiLevelNavigation";
import { PathPresentedDataModel } from "@medapro-types/common/components/PathPresenter";
import { UseVirtualList } from "@vueuse/components";
import { UseVirtualListReturn } from "@vueuse/core";
import _ from "lodash";
import { computed, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import CommonFilter from "../CommonFilter.vue";
import Icon from "../Icon.vue";
import PathPresenter from "../PathPresenter.vue";

//#region General
type ViewModel = {
  filter: string;
  selection: MultiLevelNavigationSelectionModel | null;
  branches: Branch[];
  leaves: Leaf[];
  branchPath: Branch[];
  currentBranch: Branch | null;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'MultiLevelNavigationModal',
  props: {
    value: { type: Boolean },
    modalTitle: {},
    fetchBranches: { type: Function },
    fetchLeaves: { type: Function },
    selectableType: {},
    noBreadCrumbs: { type: Boolean },
    withAll: { type: Boolean },
    selectedEntryId: {},
    selectedPath: {},
    basePathTitle: {},
    rootSelection: {}
  },
  emits: ["input", "confirm", "cancel", "close", "hide"],
  setup(__props: any, { emit: emits }) {

const props = __props;

const { t: $t } = useI18n();



//#endregion

//#region localValue
const localValue = computed({
  get: () => props.value,
  set: (nv) => emits("input", nv)
});
//#endregion

//#region Refs
const listContainer = ref<HTMLDivElement | null>(null);
const virtualList = ref<UseVirtualListReturn<unknown[]> | null>(null);
//#endregion

//#region Viewmodel
const allEntry: MultiLevelNavigationSelectionModel = {
  id: null,
  name: `<${$t("all")}>`,
  type: "all",
  dataType: AttributeDataType.AttributeDataTypeAlphanumerical
};
const defaultSelection = () => (props.withAll ? allEntry : (props.rootSelection ?? null));
const vm = reactive<ViewModel>({
  filter: "",
  selection: defaultSelection(),
  branches: [],
  leaves: [],
  branchPath: [],
  currentBranch: null
});
//#endregion

//#region Branches
const switchBranchRoot = async () => {
  vm.currentBranch = null;
  await switchBranch();
  vm.branchPath = [];
};

const switchBranchUp = async () => {
  const lastEntry = _.last(vm.branchPath);
  if (lastEntry) {
    vm.currentBranch = <Branch | null>(vm.branchPath.pop() ?? null);
    if (lastEntry.parentId) await switchBranch(lastEntry.parentId);
    else await switchBranch(_.last(vm.branchPath)?.id);
  }
};

const switchBranchDown = async (branchId: string) => {
  const _branch = vm.branches.find((x) => x.id == branchId);
  if (_branch) {
    const doSwitch = await switchBranch(branchId);
    if (doSwitch) {
      vm.currentBranch = _branch;
      vm.branchPath.push(_branch);
    }
  }
};

const switchBranch = async (branchId?: string | null | undefined) => {
  const fetchedBranches = await props.fetchBranches(branchId);
  const fetchedLeaves = await props.fetchLeaves(branchId);

  const doSwitch = !!fetchedBranches.length || !!fetchedLeaves.length;
  if (doSwitch) {
    vm.branches = fetchedBranches;
    vm.leaves = fetchedLeaves;
    vm.selection = defaultSelection();
    virtualList.value?.scrollTo(0);
  } else {
    const foundBranch = vm.branches.find((x) => x.id == branchId);
    if (foundBranch) foundBranch.hasNoChildren = true;
  }

  return doSwitch;
};

const pathPresenterData = computed<PathPresentedDataModel[]>(() => {
  const _home: PathPresentedDataModel = {
    id: "\\",
    name: props.basePathTitle ?? "",
    icon: "Home",
    click: switchBranchRoot
  };

  if (vm.branchPath && vm.branchPath.length && vm.branchPath.length > 0) _home.name = "";

  return [_home, ...vm.branchPath.map((x) => ({ id: x.id, name: x.name ?? "" }))];
});

//#endregion

//#region Selection

const selectEntry = (data: MultiLevelNavigationSelectionModel) => {
  vm.selection = data;
};

const restoreSelection = () => {
  if (props.selectedEntryId) {
    const foundIndex = combinedList.value.findIndex((x) => x.id == props.selectedEntryId);
    if (foundIndex != -1) {
      vm.selection = combinedList.value[foundIndex];
      virtualList.value?.scrollTo(foundIndex);
    }
  }
};

const hideHandler = () => {
  resetData();
  emits("hide");
};

const resetData = () => {
  Object.assign(vm, {
    filter: "",
    selection: null,
    branches: [],
    leaves: [],
    branchPath: [],
    currentBranch: null
  });
};

const confirmSelection = () => {
  const result: MultiLevelNavigationConfirmModel = { paths: vm.branchPath, selection: vm.selection };
  emits("confirm", result);
};
//#endregion

//#region Filter
const combinedList = computed<MultiLevelNavigationSelectionModel[]>(() => {
  let result = _.chain<MultiLevelNavigationSelectionModel>([]);
  if (props.withAll && _.isEmpty(vm.branchPath)) {
    result = result.concat([allEntry, { id: "-1", name: "divider", type: "divider" }]);
  }
  if (props.rootSelection) {
    result = result.concat([props.rootSelection, { id: "-1", name: "divider", type: "divider" }]);
  }
  const _branches: MultiLevelNavigationSelectionModel[] = vm.branches.map((x) => {
    return { id: x.id, name: x.name ?? "", type: "branch", hasNoChildren: x.hasNoChildren, icon: x.icon };
  });

  const _leaves: MultiLevelNavigationSelectionModel[] = vm.leaves.map((x) => {
    return { id: x.id, name: x.name, type: "leaf", dataType: x.dataType, icon: x.icon };
  });

  result = result.concat(_branches);
  if (_branches.length && _leaves.length) result = result.concat([{ id: "-1", name: "divider", type: "divider" }]);
  result = result.concat(_leaves);

  if (vm.filter?.length) result = result.filter((x) => !!x?.name && x.name.toLowerCase().includes(vm.filter.toLowerCase()));

  return result.value();
});

//#endregion

async function onShow() {
  let id: string | undefined;
  if (props.selectedPath && !_.isEmpty(props.selectedPath)) {
    const branches = _.cloneDeep(props.selectedPath.filter((x) => x.id !== ""));
    const foundBranch = _.maxBy(branches, (x) => x.depth);
    if (foundBranch) {
      vm.branchPath = branches;
      id = foundBranch.id;
    }
  }
  await switchBranch(id);

  restoreSelection();
}

return (_ctx: any,_cache: any) => {
  const _component_b_btn = _resolveComponent("b-btn")!
  const _component_b_modal = _resolveComponent("b-modal")!

  return (_openBlock(), _createBlock(_component_b_modal, {
    modelValue: localValue.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localValue).value = $event)),
    title: props.modalTitle,
    id: "simplified-tree-list-modal",
    "body-class": "simplified-tree-list-body",
    "header-close-content": "",
    onOk: confirmSelection,
    onCancel: _cache[3] || (_cache[3] = ($event: any) => (emits('cancel'))),
    onClose: _cache[4] || (_cache[4] = ($event: any) => (emits('close'))),
    onHide: hideHandler,
    onShow: onShow
  }, {
    "modal-footer": _withCtx(({ ok, cancel }) => [
      _createVNode(_component_b_btn, {
        onClick: ok,
        "data-cy": "multi-level-modal-select-btn",
        variant: "primary",
        disabled: !vm.selection || !_unref(_).includes([...(_ctx.withAll || _ctx.rootSelection ? ['all'] : []), ...props.selectableType], vm.selection.type)
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)("select")), 1)
        ]),
        _: 2
      }, 1032, ["onClick", "disabled"]),
      _createVNode(_component_b_btn, {
        onClick: cancel,
        "data-cy": "multi-level-modal-close-btn"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_unref($t)("close")), 1)
        ]),
        _: 2
      }, 1032, ["onClick"])
    ]),
    default: _withCtx(() => [
      (!props.noBreadCrumbs)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(PathPresenter, {
              reducedVisual: "",
              modelValue: pathPresenterData.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pathPresenterData).value = $event)),
              onDotsClicked: switchBranchUp,
              "dots-link-style": "",
              "data-cy": "multi-level-modal-path-presenter"
            }, null, 8, ["modelValue"])
          ]))
        : _createCommentVNode("", true),
      _createVNode(CommonFilter, {
        "no-max-width": "",
        "prepend-icon": "Filter",
        placeholder: _unref($t)('Filter'),
        modelValue: vm.filter,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((vm.filter) = $event)),
        "data-cy": "multi-level-modal-common-filter",
        "trigger-by-time": ""
      }, null, 8, ["placeholder", "modelValue"]),
      _createElementVNode("div", {
        class: "list-container",
        ref_key: "listContainer",
        ref: listContainer,
        "data-cy": "multi-level-modal-container"
      }, [
        _createVNode(_unref(UseVirtualList), {
          ref_key: "virtualList",
          ref: virtualList,
          list: combinedList.value,
          height: "calc(80vh - 150px)",
          options: { itemHeight: 30 }
        }, {
          default: _withCtx(({ data }) => [
            (data.type == 'all')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["entry", { active: vm.selection?.id == data.id }]),
                  onClick: ($event: any) => (selectEntry(data))
                }, [
                  (data.icon)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createVNode(Icon, {
                          icon: data.icon
                        }, null, 8, ["icon"])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_4, [
                    _createElementVNode("span", null, _toDisplayString(data.name), 1)
                  ])
                ], 10, _hoisted_2))
              : _createCommentVNode("", true),
            (data.type == 'branch')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  onDblclick: ($event: any) => (switchBranchDown(data.id)),
                  class: _normalizeClass(["entry branch", { active: vm.selection?.id == data.id }]),
                  onClick: ($event: any) => (selectEntry(data))
                }, [
                  (data.icon)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        _createVNode(Icon, {
                          icon: data.icon
                        }, null, 8, ["icon"])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("span", null, _toDisplayString(data.name), 1)
                  ]),
                  (data.type == 'branch' && !data.hasNoChildren)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(_component_b_btn, {
                          class: "btn-icon mono",
                          onClick: ($event: any) => (switchBranchDown(data.id))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(Icon, { icon: "ChevronRight" })
                          ]),
                          _: 2
                        }, 1032, ["onClick"])
                      ]))
                    : _createCommentVNode("", true)
                ], 42, _hoisted_5))
              : _createCommentVNode("", true),
            (data.type == 'divider')
              ? (_openBlock(), _createElementBlock("hr", _hoisted_9))
              : _createCommentVNode("", true),
            (data.type == 'leaf')
              ? (_openBlock(), _createElementBlock("div", {
                  key: 3,
                  class: _normalizeClass(["entry leaf", { active: vm.selection?.id == data.id }]),
                  onClick: ($event: any) => (selectEntry(data))
                }, [
                  (data.icon)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                        _createVNode(Icon, {
                          icon: data.icon
                        }, null, 8, ["icon"])
                      ]))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("span", null, _toDisplayString(data.name), 1)
                  ])
                ], 10, _hoisted_10))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["list"])
      ], 512)
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})